import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowRight, FaTimes } from 'react-icons/fa';
import './Services.css';
import { DotGlobeHero } from './ui/globe-hero';
import { PricingCardWithFeatures } from './ui/pricing-card-with-features';
import {
  AnimatedFeatureContainer,
  FeatureCard,
} from './ui/grid-feature-cards';
import {
  ArrowRight,
  Zap,
  Code,
  Server,
  ShieldCheck,
  Search,
  Smartphone,
} from 'lucide-react';

function splitHeroTitle(title) {
  const trimmed = title.trim();
  const i = trimmed.indexOf(' ');
  if (i === -1) return { line1: '', line2: trimmed };
  return { line1: trimmed.slice(0, i), line2: trimmed.slice(i + 1).trim() };
}

const Services = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedService, setSelectedService] = useState(null);

  const services = useMemo(
    () => [
      {
        id: 'website-design-development',
        icon: Code,
        titleKey: 'home.services.webdev.title',
        descKey: 'home.services.webdev.description',
        offering: 'webdev',
      },
      {
        id: 'hosting',
        icon: Server,
        titleKey: 'home.services.branding.title',
        descKey: 'home.services.branding.description',
        offering: 'hosting',
      },
      {
        id: 'security-audits',
        icon: ShieldCheck,
        titleKey: 'home.services.video.title',
        descKey: 'home.services.video.description',
        offering: 'security',
      },
      {
        id: 'seo',
        icon: Search,
        titleKey: 'home.services.marketing.title',
        descKey: 'home.services.marketing.description',
        offering: 'seo',
      },
      {
        id: 'mobile-apps',
        icon: Smartphone,
        titleKey: 'home.services.content.title',
        descKey: 'home.services.content.description',
        offering: 'mobile',
      },
    ],
    []
  );

  const resolvedServices = useMemo(
    () =>
      services.map((s) => ({
        id: s.id,
        icon: s.icon,
        title: t(s.titleKey),
        description: t(s.descKey),
        features: t(`services.offerings.${s.offering}.tags`, {
          returnObjects: true,
        }),
        detailedInfo: {
          overview: t(`services.offerings.${s.offering}.overview`),
          benefits: t(`services.offerings.${s.offering}.benefits`, {
            returnObjects: true,
          }),
          process: t(`services.offerings.${s.offering}.process`, {
            returnObjects: true,
          }),
          technologies: t(`services.offerings.${s.offering}.technologies`, {
            returnObjects: true,
          }),
        },
      })),
    [t, services]
  );

  const handleServiceClick = (service) => {
    setSelectedService(service);
  };

  const closeModal = () => {
    setSelectedService(null);
  };

  const scrollToServiceOfferings = () => {
    document
      .getElementById('services-offerings')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const { line1, line2 } = splitHeroTitle(t('services.hero.title'));

  return (
    <div className="services">
      {/* Hero — wireframe globe */}
      <DotGlobeHero
        rotationSpeed={0.004}
        globeRadius={1}
        className="bg-black"
      >
        <div className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-t from-black/80 via-black/35 to-black/65" />

        <div className="pointer-events-none absolute top-1/4 left-[10%] h-72 w-72 rounded-full bg-primary/10 blur-3xl md:left-1/4" />
        <div className="pointer-events-none absolute bottom-[15%] right-[10%] h-52 w-52 rounded-full bg-primary/10 blur-3xl md:right-1/4" />

        <div className="relative z-10 mx-auto w-full max-w-5xl space-y-10 px-2 py-4 text-center text-foreground md:space-y-12">
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75 }}
            className="space-y-8"
          >
            <div className="relative inline-flex items-center gap-2 rounded-full border border-primary/35 bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 px-5 py-2.5 text-primary shadow-lg backdrop-blur-xl md:gap-3 md:px-6 md:py-3">
              <span
                className="h-1.5 w-1.5 rounded-full bg-primary"
                aria-hidden
              />
              <span className="relative text-xs font-bold uppercase tracking-[0.2em] md:text-sm">
                {t('services.hero.badge')}
              </span>
              <span
                className="h-1.5 w-1.5 rounded-full bg-primary"
                aria-hidden
              />
            </div>

            <div className="space-y-4 md:space-y-6">
              <h1
                className="select-none font-black tracking-tighter"
                style={{
                  fontFamily: 'Inter, system-ui, sans-serif',
                  lineHeight: 0.9,
                  fontSize: 'clamp(2.25rem, 8vw, 7rem)',
                }}
              >
                {line1 && (
                  <span className="mb-3 block font-light text-foreground/70 md:mb-4">
                    {line1}
                  </span>
                )}
                <span className="relative inline-block font-black text-white [text-shadow:0_0_48px_rgba(255,255,255,0.12)]">
                  {line2}
                </span>
              </h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.35 }}
                className="mx-auto max-w-3xl text-lg font-medium leading-relaxed text-muted-foreground md:text-xl"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              >
                {t('services.hero.subtitle')}
              </motion.p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="flex flex-col items-center justify-center gap-4 pt-2 sm:flex-row sm:gap-5"
          >
            <motion.button
              type="button"
              onClick={() => navigate('/start-project')}
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="group relative inline-flex items-center gap-2 overflow-hidden rounded-xl border border-primary/25 bg-gradient-to-r from-primary via-primary to-primary/90 px-7 py-3.5 text-base font-semibold text-primary-foreground shadow-xl md:gap-3 md:px-8 md:py-4 md:text-lg"
            >
              <span className="relative z-10 tracking-wide">
                {t('services.cta.button')}
              </span>
              <ArrowRight className="relative z-10 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
            </motion.button>

            <motion.button
              type="button"
              onClick={scrollToServiceOfferings}
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="group relative inline-flex items-center gap-2 overflow-hidden rounded-xl border-2 border-border/45 bg-background/70 px-7 py-3.5 text-base font-semibold backdrop-blur-xl transition-colors duration-300 hover:border-primary/40 hover:bg-background/90 md:gap-3 md:px-8 md:py-4 md:text-lg"
            >
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-primary/10 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              <Zap className="relative z-10 h-5 w-5" />
              <span className="relative z-10 tracking-wide">
                {t('services.hero.viewAll')}
              </span>
            </motion.button>
          </motion.div>
        </div>
      </DotGlobeHero>

      <section id="services-offerings" className="services-offerings-section">
        <div className="mx-auto w-full max-w-5xl space-y-8 px-4 py-16 md:py-32">
          <AnimatedFeatureContainer className="mx-auto max-w-3xl text-center">
            <h2 className="text-balance text-3xl font-bold tracking-wide md:text-4xl lg:text-5xl xl:font-extrabold">
              {t('services.offeringGrid.title')}
            </h2>
            <p className="mt-4 text-balance text-sm tracking-wide text-muted-foreground md:text-base">
              {t('services.offeringGrid.subtitle')}
            </p>
          </AnimatedFeatureContainer>

          <AnimatedFeatureContainer
            delay={0.4}
            className="grid grid-cols-1 divide-x divide-y divide-dashed border border-dashed border-border/50 sm:grid-cols-2 lg:grid-cols-3"
          >
            {resolvedServices.map((service) => {
              const FeatureIcon = service.icon;
              return (
                <FeatureCard
                  key={service.id}
                  feature={{
                    title: service.title,
                    description: service.description,
                    icon: FeatureIcon,
                  }}
                  tags={service.features}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleServiceClick(service)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleServiceClick(service);
                    }
                  }}
                  className="cursor-pointer rounded-none text-left outline-none ring-offset-background transition-colors hover:bg-muted/10 focus-visible:ring-2 focus-visible:ring-ring"
                />
              );
            })}
          </AnimatedFeatureContainer>
        </div>
      </section>

      <AnimatePresence>
        {selectedService && (
          <motion.div
            className="service-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="service-modal service-modal--detail-card"
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 50 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button type="button" className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>

              <PricingCardWithFeatures
                title={selectedService.title}
                subtitle={selectedService.description}
                features={selectedService.features.map((label, idx) => {
                  const b = selectedService.detailedInfo.benefits[idx];
                  return {
                    label,
                    info: b ?? selectedService.detailedInfo.overview,
                  };
                })}
                startingLabel={t('services.detailCard.startingLabel')}
                priceDisplay={t('services.detailCard.priceCustom')}
                ctaLabel={t('services.detailCard.bookCall')}
                onCtaClick={() => {
                  closeModal();
                  navigate('/start-project');
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="services-cta">
        <motion.div
          className="cta-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="services-cta-title-blackletter">
            {t('services.cta.title')}
          </h2>
          <p>{t('services.cta.subtitle')}</p>
          <motion.button
            type="button"
            className="cta-button"
            onClick={() => navigate('/start-project')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('services.cta.button')}
            <FaArrowRight className="arrow-icon" />
          </motion.button>
        </motion.div>
      </section>
    </div>
  );
};

export default Services;
